import {useState, useEffect, useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Helmet} from 'react-helmet';
// eslint-disable-next-line import/no-cycle
import StepList from 'SbsSuite/Presentation/StepList';
import SbsProperties from 'SbsSuite/Presentation/SbsProperties';
import ProcessOutcomeLobbyContainer from 'ProcessOutcomeLobby/Container/ProcessOutcomeLobbyContainer';
import CreateUpdateStepPortlet from 'StepUpdateCreate/Components/CreateUpdateStepPortlet';
import {
  deleteStep,
  findStepById,
  findStepsBySbs,
  saveStepAfter,
  saveStepConsumable,
} from 'StepUpdateCreate/service/StepService';
import {calculateTotalSbsTime, canWrite} from 'Sbs/service/SbsService';

export default function StepListContainer(props, context) {
  const {
    isSubSbs,
    sbs,
    location,
    onAddToSchedule,
    duplicationMode,
    duplicationSelectedSteps,
    onDuplicationToggleSelectedStep,
    onSaveStep,
    onCancelStep,
    parentSbss,
    onDeleteSbs,
    onToggleDuplicationMode,
    onToggleSelectAllSteps,
    onDuplicateSteps,
    isLoadAll,
    onDeleteFirstStep,
  } = props;
  const {id} = sbs;
  const {router} = context;
  const {pathname, query} = location;
  const [isLoading, setIsLoading] = useState(true);
  const [steps, setSteps] = useState([]);
  const [subSbssLoaded, setSubSbssLoaded] = useState({});
  const [loadAllSubSbs, setLoadAllSubSbs] = useState(isLoadAll);
  const [
    openStepConsumableCreateDialogBool,
    setOpenStepConsumableCreateDialogBool,
  ] = useState(false);
  const [selectedAddConsumableStepId, setSelectedAddConsumableStepId] =
    useState('');
  const [canWriteSbs, setCanWriteSbs] = useState(false);
  const sbsTotalTime = useMemo(() => {
    const {hh, mm, ss} = calculateTotalSbsTime(steps);

    return `${hh}h ${mm}m ${ss}s`;
  }, [steps]);
  const hasSubSbs = useMemo(
    () => Object.entries(subSbssLoaded).length > 0,
    [subSbssLoaded],
  );
  const subSbsLoaded = useMemo(
    () => Object.entries(subSbssLoaded).some(([key, value]) => value),
    [subSbssLoaded],
  );
  const handleRetrieveSteps = async (sbs, startStepId) =>
    await findStepsBySbs(sbs.id, startStepId);
  const handleAddStepAfterRpc = async (step, previousStepId, decision) =>
    await saveStepAfter(step.id, previousStepId, decision);
  const handleRetrieveFirstSteps = useCallback(async () => {
    const newSteps = await handleRetrieveSteps(sbs);

    setSteps(newSteps);
    setIsLoading(false);
  }, [sbs]);
  const handleEditStep = (step) => {
    router.replace({
      pathname,
      query: {...query, editStepId: step.id},
    });
  };
  const handleLoadUnloadAllSubSbs = () =>
    subSbsLoaded ? handleUnloadAllSubSbs() : handleLoadAllSubSbs();
  const handleLoadAllSubSbs = () => {
    setLoadAllSubSbs(true);
    for (const step of steps) {
      if (step.subSbs !== null)
        setSubSbssLoaded((existing) => ({
          ...existing,
          [step.id]: true,
        }));
    }
  };
  const handleUnloadAllSubSbs = () => {
    setLoadAllSubSbs(false);
    for (const step of steps) {
      if (step.subSbs !== null)
        setSubSbssLoaded((existing) => ({
          ...existing,
          [step.id]: false,
        }));
    }
  };
  const handleDeleteStep = async (step) => {
    if (
      // TODO: implement a better alert system
      // eslint-disable-next-line no-restricted-globals, no-alert
      confirm(`Are you sure you want to delete this ${step.stepType.type}?`)
    ) {
      setSteps((prevSteps) =>
        prevSteps.filter((prevStep) => prevStep.id !== step.id),
      );

      await deleteStep(step);
    }
  };
  const handleAddStepAfter = (previousStepIndex, decision) =>
    setSteps((prevSteps) => {
      const newSteps = [...prevSteps];

      newSteps.splice(previousStepIndex + 1, 0, {decision});

      return newSteps;
    });
  const handleLoadSubSbs = (step) =>
    setSubSbssLoaded((existing) => ({
      ...existing,
      [step.id]: !existing[step.id],
    }));
  const swapSteps = (firstStepIndex, secondStepIndex) =>
    setSteps((existing) => {
      const newSteps = [...existing];
      const step = newSteps[firstStepIndex];

      newSteps[firstStepIndex] = newSteps[secondStepIndex];
      newSteps[secondStepIndex] = step;

      return newSteps;
    });
  const handleMoveStepUp = async (index) => {
    const previousStep = index - 2 >= 0 ? steps[index - 2] : null;

    await handleAddStepAfterRpc(steps[index], previousStep && previousStep.id);
    swapSteps(index - 1, index);
  };
  const handleClickDecision = async (decisionStepIndex, nextStep) => {
    const steps = await handleRetrieveSteps(sbs, nextStep.id);

    setSteps((prevSteps) => [
      ...prevSteps.slice(0, decisionStepIndex + 1),
      ...steps,
    ]);
  };
  // close the step consumable dialog box
  const handleCloseStepConsumableCreateDialog = () =>
    setOpenStepConsumableCreateDialogBool(false);
  // open the step consumable create dialog box
  const handleOpenStepConsumablesCreateDialog = (step) => {
    setOpenStepConsumableCreateDialogBool(true);
    setSelectedAddConsumableStepId(step.id);
  };
  // save step-consumable to database
  const handleSaveStepConsumable = async (obj) => {
    // save the step-consumable only if all fields have values

    await saveStepConsumable({
      ...obj,
      stepConsumables: {id: selectedAddConsumableStepId},
    });
    // update the step consumables by fetching the step
    await handleRetrieveFirstSteps(sbs);

    setOpenStepConsumableCreateDialogBool(false);
  };
  const handleSaveStep = async (savedStepId, decision, index) => {
    const savedStep = await findStepById(savedStepId);
    const previousStep = index === 0 ? {} : steps[index - 1];

    setSteps((prevSteps) => {
      const newSteps = [...prevSteps];

      newSteps[index] = savedStep;

      return newSteps;
    });

    // This if is used to tell if creating or updating the step.
    // In StepList created steps have a value of step.decision and updated no not
    if (decision != null) {
      const result = await handleAddStepAfterRpc(
        savedStep,
        previousStep.id,
        decision,
      );

      if (previousStep.stepType.type === 'Decision') {
        handleClickDecision(index - 1, result);
      }
    } else {
      delete query.editStepId;

      router.replace({
        pathname,
        query,
      });
    }
  };
  const handleCancelStep = (index) => {
    if (steps[index].id) {
      delete query.editStepId;

      router.replace({
        pathname,
        query,
      });
    } else {
      setSteps((prevSteps) => {
        const tempSteps = [...prevSteps];

        tempSteps.splice(index, 1);

        return tempSteps;
      });
    }
  };

  useEffect(() => {
    if (!!sbs) {
      handleRetrieveFirstSteps();
    } else {
      setIsLoading(false);
    }
  }, [handleRetrieveFirstSteps, sbs]);

  useEffect(() => {
    if (steps.length > 0) {
      if (loadAllSubSbs) {
        handleLoadAllSubSbs();
      } else {
        handleUnloadAllSubSbs();
      }
    }
  }, [loadAllSubSbs, steps]);

  useEffect(() => {
    if (!isLoading && steps.length === 0 && onDeleteFirstStep) {
      onDeleteFirstStep();
    }
  }, [steps.length, isLoading, onDeleteFirstStep]);

  useEffect(() => {
    const checkWritePermission = async () => {
      if (sbs?.id) {
        try {
          const hasWritePermission = await canWrite(sbs.id);

          setCanWriteSbs(hasWritePermission === true);
        } catch (error) {
          console.error('Error checking write permission:', error);
          setCanWriteSbs(false);
        }
      } else {
        setCanWriteSbs(false);
      }
    };

    checkWritePermission();
  }, [sbs?.id]);

  return (
    !isLoading && (
      <>
        {!isSubSbs && (
          <>
            <Helmet title={sbs.title} defer={false} />
            <SbsProperties
              hasSubSbs={hasSubSbs}
              sbsTotalTime={sbsTotalTime}
              sbs={sbs}
              parentSbss={parentSbss}
              onDeleteSbs={onDeleteSbs}
              duplicationMode={duplicationMode}
              onToggleDuplicationMode={onToggleDuplicationMode}
              duplicationSelectedSteps={duplicationSelectedSteps}
              onToggleSelectAllSteps={onToggleSelectAllSteps}
              onDuplicateSteps={onDuplicateSteps}
              subSbsLoaded={subSbsLoaded}
              onLoadUnloadAllSubSbs={handleLoadUnloadAllSubSbs}
            />
          </>
        )}
        {sbs.id &&
          (!sbs.firstStep && !isSubSbs ? (
            <CreateUpdateStepPortlet
              sbs={sbs}
              onSave={onSaveStep}
              onCancel={onCancelStep}
            />
          ) : (
            <StepList
              location={location}
              isLoadAll={loadAllSubSbs}
              sbs={sbs}
              steps={steps}
              subSbssLoaded={subSbssLoaded}
              onEditStep={canWriteSbs ? handleEditStep : undefined}
              onDeleteStep={canWriteSbs ? handleDeleteStep : undefined}
              onAddStepAfter={canWriteSbs ? handleAddStepAfter : undefined}
              onLoadSubSbs={handleLoadSubSbs}
              onAddToSchedule={onAddToSchedule}
              onMoveStepUp={canWriteSbs ? handleMoveStepUp : undefined}
              onClickDecision={handleClickDecision}
              duplicationMode={duplicationMode}
              duplicationSelectedSteps={duplicationSelectedSteps}
              onDuplicationToggleSelectedStep={onDuplicationToggleSelectedStep}
              onSaveStep={handleSaveStep}
              onCancelStep={handleCancelStep}
              openStepConsumableCreateDialogBool={
                openStepConsumableCreateDialogBool
              }
              onOpenStepConsumableCreateDialog={
                canWriteSbs ? handleOpenStepConsumablesCreateDialog : undefined
              }
              onCloseStepConsumableCreateDialog={
                handleCloseStepConsumableCreateDialog
              }
              onSaveStepConsumableDialog={handleSaveStepConsumable}
            />
          ))}
        {sbs.id && !isSubSbs && (
          <ProcessOutcomeLobbyContainer location={location} sbs={sbs} />
        )}
      </>
    )
  );
}

StepListContainer.propTypes = {
  isSubSbs: PropTypes.bool,
  location: PropTypes.shape({
    query: PropTypes.object,
    pathname: PropTypes.string,
  }).isRequired,
  sbs: PropTypes.shape({
    firstStep: PropTypes.shape({
      id: PropTypes.string,
    }),
    id: PropTypes.string,
    title: PropTypes.string,
    sbsStatus: PropTypes.shape({
      id: PropTypes.string,
      status: PropTypes.string,
    }),
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
  }).isRequired,
  duplicationMode: PropTypes.bool,
  duplicationSelectedSteps: PropTypes.array,
  onDuplicationToggleSelectedStep: PropTypes.func,
  onSaveStep: PropTypes.func.isRequired,
  onCancelStep: PropTypes.func.isRequired,
  onAddToSchedule: PropTypes.func,
  parentSbss: PropTypes.array,
  onDeleteSbs: PropTypes.func,
  onToggleDuplicationMode: PropTypes.func,
  onToggleSelectAllSteps: PropTypes.func,
  onDuplicateSteps: PropTypes.func,
  isLoadAll: PropTypes.bool,
  onDeleteFirstStep: PropTypes.func.isRequired,
};

StepListContainer.contextTypes = {
  router: PropTypes.object.isRequired,
};

StepListContainer.defaultProps = {
  duplicationMode: false,
  duplicationSelectedSteps: [],
  onDuplicationToggleSelectedStep: undefined,
  subSbs: false,
  isLoadAll: false,
};
